import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { TLngLat } from 'App/Map/types'
import { HighchartsChart } from 'components/HighchartsChart/HighchartsChart'
import type { TSemiosCustomHighchartsDateFormats } from 'components/HighchartsChart/_utils/setCustomHighchartsDateFormats'
import { baseChartOptions } from 'components/StackedChart/_utils/baseChartOptions'
import { baseLineChartOptions } from 'components/StackedChart/_utils/baseLineChartOptions'
import { translate } from 'i18n/i18n'
import { merge } from 'lodash'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { upperYAxisTitleMaker } from 'utils/upperYAxisTitleMaker'
import { getPropertyForSelectedStation } from '../_utils/getPropertyForSelectedStation'
import { pwpFcSeriesMaker } from '../_utils/pwpFcSeriesMaker'

export const VWCChart = ({
  dates,
  fc,
  lastGoodReading,
  pwp,
  selectedStation,
  vwcData,
}: {
  dates: [Date | null, Date | null]
  vwcData?:
    | NonNullable<NonNullable<routes.Values.Response['points']>[string]['values']['soilVwc']>[number]
    | null
  pwp: number | ''
  fc: number | ''
  lastGoodReading?: routes.SoilCalibrationSettingsGet.Response['lastGoodReading']
  selectedStation: TLngLat | null
}) => {
  const property = getPropertyForSelectedStation(selectedStation)
  const timezone = property?.timezone ?? moment.tz.guess()

  const data = (vwcData?.timeseries ?? [])
    .map(({ value, timestamp }) => {
      if (typeof value !== 'number') {
        return [+moment.tz(timestamp, timezone), value ?? null]
      }

      return [+moment.tz(timestamp, timezone), value]
    })
    .filter(
      (point): point is [number, NonNullable<typeof point>[1]] =>
        Array.isArray(point) && point.length === 2 && typeof point[0] === 'number',
    )

  const pwpSeries = pwpFcSeriesMaker({
    data: pwp ? data : [],
    name: translate.phrases.banyanApp('Permanent Wilting Point (PWP)'),
    color: colors.red,
    fixedValue: pwp ? pwp : 0,
  })

  const fcSeries = pwpFcSeriesMaker({
    data: fc ? data : [],
    name: translate.phrases.banyanApp('Field Capacity (FC)'),
    color: colors.blue,
    fixedValue: fc ? fc : 0,
  })

  const series = [
    {
      id: 'vwc',
      color: colors.midnight,
      data,
      name: translate.phrases.banyanApp('Soil Moisture'),
      type: 'line',
      visible: true,
      yAxis: 0,
      connectNulls: false,
      tooltip: {
        valueSuffix: unitConverter.soilVwc().suffix(),
        valueDecimals: unitConverter.soilVwc().defaultNumberOfDecimalPlaces(),
      },
    },
    fcSeries,
    pwpSeries,
  ]

  const chartOptions = merge(baseChartOptions(timezone), baseLineChartOptions(), {
    semiosHighchartsAdditions: {
      id: 'soil-calibration-chart',
      firstForecastTimestamp: +new Date(),
    },
    chart: {
      backgroundColor: data.length ? 'transparent' : colors.grey50,
      zoomType: null,
      selection: {
        enabled: false,
      },
      events: {
        selection: function (event: { preventDefault: () => void }) {
          event.preventDefault()

          return false
        },
        load: function (this: Highcharts.Chart) {
          this.container.onmouseleave = () => this.tooltip.hide()
        },
      },
    },
    dragDrop: {
      draggableX: false,
      draggableY: false,
    },
    tooltip: {
      xDateFormat: '%Z' as TSemiosCustomHighchartsDateFormats,
    },
    yAxis: {
      title: upperYAxisTitleMaker(unitConverter.soilVwc().categoryTitle()),
      visible: data.length,
      softMin: pwp && fc ? Math.min(pwp, fc) : undefined,
      softMax: pwp && fc ? Math.max(pwp, fc) : undefined,
    },
    xAxis: {
      softMin: dates[0] ? moment.tz(dates[0], timezone).valueOf() : undefined,
      softMax: dates[1] ? moment.tz(dates[1], timezone).valueOf() : undefined,
      type: 'datetime',
      startOnTick: false,
      endOnTick: false,
      plotBands: null, // prevent forecast from showing up
      plotLines: lastGoodReading
        ? [
            {
              color: colors.green,
              value: +moment.tz(lastGoodReading, timezone),
              width: 2,
              label: {
                text: translate.phrases.banyanApp('First full day of good readings'),
              },
            },
          ]
        : [],
    },
    legend: {
      enabled: false,
    },
    timezone,
    plotOptions: {
      series: {
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
    series,
  })

  return <HighchartsChart css={{ height: 300 }} chartOptions={chartOptions} />
}
